import { changePassword } from 'aws-auth-lib';
import { ensureAuthSuccess } from '../utils/authResult';
import { validateProfilePicture } from '../utils/selfProfilePicture';
import { isDate, localToday } from '../utils/selfProfile';
import { genderOptions, sexOptions } from '../configs/selfProfile';
import { api, getPanelRole } from './api';

const roleHeaders = (role) => {
    if (!role?.uuid || role.disabled || getPanelRole() !== role.uuid) {
        throw new Error('PROFILE_CONTEXT_CHANGED');
    }
    return { 'CHEART-ROLE': role.uuid };
};

export const getOwnProfile = (role, signal) =>
    api.get('/cheart/user', { headers: roleHeaders(role), signal });

export const updateOwnProfile = async (payload, role) => {
    const headers = roleHeaders(role);
    const fields = [
        'name',
        'birth_date',
        'sex',
        'gender',
        'locale',
        'timezone',
        'phone',
        'address'
    ];
    if (Object.keys(payload).some((key) => !fields.includes(key)))
        throw new Error('INVALID_PROFILE_PATCH');
    for (const [key, value] of Object.entries(payload)) {
        if (key !== 'address' && typeof value !== 'string')
            throw new Error('INVALID_PROFILE_PATCH');
    }
    if ('name' in payload && (!payload.name.trim() || payload.name.length > 80))
        throw new Error('INVALID_PROFILE_PATCH');
    if ('phone' in payload && payload.phone.length > 20)
        throw new Error('INVALID_PROFILE_PATCH');
    if (
        'birth_date' in payload &&
        (!isDate(payload.birth_date) || payload.birth_date > localToday())
    )
        throw new Error('INVALID_PROFILE_PATCH');
    if ('sex' in payload && !sexOptions.some((option) => option.value === payload.sex))
        throw new Error('INVALID_PROFILE_PATCH');
    if (
        'gender' in payload &&
        !genderOptions.some((option) => option.value === payload.gender)
    )
        throw new Error('INVALID_PROFILE_PATCH');
    if ('timezone' in payload)
        new Intl.DateTimeFormat('pt-PT', { timeZone: payload.timezone });
    if ('address' in payload) {
        const limits = { street: 200, zip: 15, city: 30, province: 30, country: 2 };
        if (
            !payload.address ||
            Array.isArray(payload.address) ||
            typeof payload.address !== 'object'
        )
            throw new Error('INVALID_PROFILE_PATCH');
        for (const [key, value] of Object.entries(payload.address)) {
            if (
                !Object.prototype.hasOwnProperty.call(limits, key) ||
                typeof value !== 'string' ||
                value.length > limits[key] ||
                (key === 'country' && !/^[A-Z]{2}$/.test(value))
            )
                throw new Error('INVALID_PROFILE_PATCH');
        }
    }
    return api.patch('/cheart/user', payload, { headers });
};

export const uploadOwnPicture = async (file, role) => {
    const validationError = validateProfilePicture(file);
    if (validationError) throw new Error(validationError);
    const body = new FormData();
    body.append('picture', file);
    const { data } = await api.post('/cheart/user/picture', body, {
        headers: { ...roleHeaders(role), 'Content-Type': null }
    });
    if (typeof data?.picture !== 'string' || !data.picture)
        throw new Error('PICTURE_UNAVAILABLE');
    return data.picture;
};

export const changeOwnPassword = async (currentPassword, newPassword) =>
    ensureAuthSuccess(await changePassword(currentPassword, newPassword));

export const profileErrorMessage = (error) => {
    if (error?.response?.status === 401)
        return 'A sessão expirou. Volte a iniciar sessão.';
    if (error?.response?.status === 403)
        return 'Não foi possível autorizar a alteração do perfil.';
    if (error?.response?.status === 400)
        return 'Não foi possível guardar. Verifique os dados introduzidos.';
    return 'Não foi possível guardar os dados. Verifique a ligação e tente novamente.';
};

export const passwordErrorMessage = (error) => {
    if (error?.name === 'InvalidPasswordException')
        return 'A nova palavra-passe não cumpre os requisitos de segurança da conta.';
    if (error?.name === 'PasswordHistoryPolicyViolationException')
        return 'Escolha uma palavra-passe que não tenha utilizado recentemente.';
    if (error?.name === 'NotAuthorizedException')
        return 'Não foi possível confirmar a palavra-passe atual. Verifique-a ou volte a iniciar sessão.';
    if (['LimitExceededException', 'TooManyRequestsException'].includes(error?.name))
        return 'Foram feitas demasiadas tentativas. Aguarde antes de tentar novamente.';
    return 'Não foi possível alterar a palavra-passe. Tente novamente.';
};
